'use client'

import { useState, useEffect } from 'react'
import { Badge } from '@/ui'
import { updateUserTransaction } from '@/features/transactions/transactions.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  TransactionOrSplit,
  TransactionType,
} from '@/reducers/admin/allTransactions.slice'

import { selectIsLockedTransaction } from '@/features/transactions/transactions.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { getTransactionCategorySelector } from '@/features/reports/reports.selectors'
import { TransactionCategoryDropdown } from './TransactionCategoryDropdown'
import { fetchChangeLogsForUserTransaction } from '@/features/admin/transaction-change-logs/transactionChangeLog.slice'
import { categoryLastChangedByBookkeeper } from '@/features/transactions/helpers'
import { RecategorizationModal } from '@/features/transactions/RecategorizationModal'
import { getCategoryBadgeStyle } from '@/src/constants/colorConstants'

interface Props {
  transaction: TransactionOrSplit
  editable?: boolean
  useRecategorizationModal?: boolean
  variant?: 'default' | 'text'
  filtered?: boolean
}

export const TransactionCategoryLabelReadOnly = ({
  transactionType,
  transactionCategoryId,
  variant = 'default',
}: {
  transactionType: TransactionType
  transactionCategoryId: number | null
} & Pick<Props, 'variant'>) => {
  const category = useReselector(
    getTransactionCategorySelector,
    transactionCategoryId
  )

  const text =
    transactionType === 'personal' ? '--' : category?.name || 'Unselected'

  const categoryKey =
    category?.name ?? String(transactionCategoryId ?? 'uncategorized')
  const categoryBadgeStyle = getCategoryBadgeStyle(categoryKey)

  return (
    <span>
      {variant === 'default' ? (
        <Badge
          variant={categoryBadgeStyle.variant}
          color={categoryBadgeStyle.color}
        >
          {text}
        </Badge>
      ) : (
        <span className="text-sm font-medium text-neutral-600">{text}</span>
      )}
    </span>
  )
}

export const TransactionCategoryLabel = ({
  transaction,
  editable,
  useRecategorizationModal = false,
  variant = 'default',
  filtered = false,
}: Props) => {
  const dispatch = useAppDispatch()
  const [updating, setUpdating] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )

  const [transactionCategoryId, setTransactionCategoryId] = useState<
    number | null
  >(null)
  const category = useReselector(
    getTransactionCategorySelector,
    transaction.transactionCategoryId
  )

  useEffect(() => {
    setTransactionCategoryId(transaction.transactionCategoryId)
  }, [transaction])

  const updateTransactionCategory = async (value: number | null) => {
    setUpdating(true)
    const updated = await updateUserTransaction(transaction.id, {
      transactionCategoryId: value,
    })(dispatch)
    if (updated) {
      setUpdating(false)
    }
  }

  const confirmChange = async (value: number | null) => {
    setTransactionCategoryId(value)
    if (useRecategorizationModal) {
      const changeLogs = await fetchChangeLogsForUserTransaction({
        transactionId: transaction.id,
      })(dispatch)
      const wasBookkeeper = categoryLastChangedByBookkeeper(
        transaction.userId,
        changeLogs
      )
      if (wasBookkeeper) {
        setOpenModal(true)
        return
      }
    }
    updateTransactionCategory(value)
    return
  }

  const showDropdown =
    editable && !locked && !transaction.type?.includes('personal')

  if (showDropdown) {
    return (
      <>
        <TransactionCategoryDropdown
          placeholder="Search categories..."
          optionStyle="normal"
          value={transactionCategoryId ?? undefined}
          transactionAmount={filtered ? transaction.amountInCents : undefined}
          transactionDate={transaction.date}
          onChange={confirmChange}
          isTaxCompliant={
            variant === 'text' ? category?.isTaxCompliant : undefined
          }
          disabled={updating}
          buttonWidthClassName="w-full"
        />

        <RecategorizationModal
          oldCategoryId={transaction.transactionCategoryId}
          newCategoryId={transactionCategoryId}
          open={openModal}
          close={() => {
            setOpenModal(false)
            setTransactionCategoryId(transaction.transactionCategoryId)
          }}
          updateAction={() => updateTransactionCategory(transactionCategoryId)}
        />
      </>
    )
  }

  return (
    <TransactionCategoryLabelReadOnly
      transactionType={transaction.type}
      transactionCategoryId={transaction.transactionCategoryId}
      variant={variant}
    />
  )
}
